import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import './Header.css';

const Header = () => {
  const { user, signOut } = useAuth();
  const { isUsingSupabaseData, getUserNotifications, getUserData } = useData();
  const navigate = useNavigate();
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const loadUserData = async () => {
      if (user?.id) {
        try {
          // Load user data from database (including profile picture)
          const dbUserData = await getUserData(user.id);
          setUserData(dbUserData);
        } catch (error) {
          console.error('Error loading user data for header:', error);
        }
      }
    };

    loadUserData();

    // Listen for user data updates (e.g., when profile picture is uploaded)
    const handleUserDataUpdate = (event: CustomEvent) => {
      if (event.detail?.userId === user?.id) {
        loadUserData();
      }
    };

    window.addEventListener('userDataUpdated', handleUserDataUpdate as EventListener);

    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate as EventListener);
    };
  }, [user?.id, getUserData]);

  useEffect(() => {
    const loadUnreadCount = async () => {
      if (user) {
        try {
          const notifications = await getUserNotifications(user.id);
          // Calculate total unread messages from notifications
          const total = notifications.reduce((sum, notification) => {
            return sum + (notification.unreadCount || 0);
          }, 0);
          setTotalUnreadMessages(total);
        } catch (error) {
          console.error('Error loading unread message count:', error);
          setTotalUnreadMessages(0);
        }
      } else {
        setTotalUnreadMessages(0);
      }
    };

    loadUnreadCount();
  }, [user, getUserNotifications]);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <Link to="/" className="brand">
            <img src="/logo192.png" alt="Share My Shed" className="header-logo" />
            Share My Shed
          </Link>
          
          <nav className="nav-links">
            <Link to="/category/all" className="nav-link">Browse Items</Link>
            <Link to="/about" className="nav-link">About</Link>
            <Link to="/help" className="nav-link">Help</Link>
            {!isUsingSupabaseData && (
              <span className="data-source-indicator mock">
                📝 Mock Data
              </span>
            )}
          </nav>
        </div>
        
        <div className="user-section">
          {user ? (
            <>
              <Link to="/notifications" className="nav-link">
                Notifications
                {totalUnreadMessages > 0 && (
                  <span className="notification-badge">{totalUnreadMessages}</span>
                )}
              </Link>
              
              <div className="user-info">
                <img 
                  src={userData?.picture || user.user_metadata?.picture || '/default-avatar.svg'} 
                  alt="User Avatar" 
                  className="user-avatar"
                />
                <Link to="/profile" className="user-name" data-testid="user-name-link">
                  {userData?.name || user.user_metadata?.name || user.email}
                </Link>
              </div>
              
              <div className="user-menu">
                <Link to="/my-items" className="nav-link">My Items</Link>
                <Link to="/my-rentals" className="nav-link">My Rentals</Link>
                <button onClick={handleSignOut} className="sign-out-button">
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            <div className="auth-buttons">
              <button onClick={handleSignIn} className="login-button">
                Log In
              </button>
              <button onClick={handleSignIn} className="signup-button">
                Sign Up
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 